import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Tag } from "antd";
import { WarningOutlined } from "@ant-design/icons";

import { apiGetUserStrategies } from "api/apiStrategies";
import {
  apiGetOptionStrategies,
  apiGetOptionTrades,
} from "api/apiOptionStrategies";
import StrategyLink from "./StrategyLink";
import { useTradingRefresh } from "./tradingRefresh";

// most urgent first
const KINDS = {
  closeFailed: {
    color: "red",
    text: "Close failed",
    hint: "AutoTrader couldn't close it; close it yourself",
  },
  approval: {
    color: "gold",
    text: "Approve buy",
    hint: "Waiting for your approval (semi automation)",
  },
  exit: { color: "orange", text: "Exit advised", hint: "Confirm your sell" },
  buy: { color: "blue", text: "Buy signal", hint: "Accept or dismiss" },
};
const ORDER = Object.keys(KINDS);

// what the user has to act on; automated strategies' buys and exits are
// AutoTrader's (demo strategies are never automated)
const kindOf = (trade, automated) => {
  if (trade.orderStatus === "automated-close-failed") return "closeFailed";
  if (trade.approval?.status === "awaiting") return "approval";
  if (automated) return null;
  if (trade.orderStatus === "pending-close") return "exit";
  if (trade.orderStatus === "pending") return "buy";
  return null;
};

// "Needs your attention" list under the KPIs, for stocks and options alike.
// Clicking a strategy opens it, where the buttons for each action are.
const PendingActionsBar = () => {
  const accountType = useSelector((state) => state.main.accountType);
  const { refreshKey } = useTradingRefresh();
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const [stockStrategies, optionStrategies, optionTrades] =
        await Promise.all([
          apiGetUserStrategies({ accountType }),
          apiGetOptionStrategies(),
          apiGetOptionTrades(),
        ]);

      const next = [];
      stockStrategies.forEach((strategy) => {
        const automated = strategy.automated && accountType === "real";
        [...strategy.pendingBuys, ...strategy.pendingSells].forEach((trade) => {
          const kind = kindOf(trade, automated);
          if (kind) next.push({ kind, trade, strategy, instrument: "stocks" });
        });
      });

      const optionStrategyById = Object.fromEntries(
        optionStrategies.map((strategy) => [strategy._id, strategy]),
      );
      optionTrades
        .filter((trade) => trade.accountType === accountType)
        .forEach((trade) => {
          const strategy = optionStrategyById[trade.strategy?._id];
          if (!strategy) return;
          const kind = kindOf(trade, strategy.automated);
          if (kind) next.push({ kind, trade, strategy, instrument: "options" });
        });

      next.sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind));
      setItems(next);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountType, refreshKey]);

  const counts = useMemo(
    () =>
      ORDER.map((kind) => ({
        kind,
        count: items.filter((item) => item.kind === kind).length,
      })).filter(({ count }) => count),
    [items],
  );

  if (!items.length) return null;

  return (
    <div className="pending-actions">
      <div className="pending-actions-header">
        <WarningOutlined />
        <b>Needs your attention</b>
        {counts.map(({ kind, count }) => (
          <Tag key={kind} color={KINDS[kind].color}>
            {count} {KINDS[kind].text}
          </Tag>
        ))}
      </div>
      <ul className="pending-actions-list">
        {items.map(({ kind, trade, strategy, instrument }) => (
          <li key={trade._id}>
            <Tag color={KINDS[kind].color}>{KINDS[kind].text}</Tag>
            <span className="pending-actions-instrument">
              {instrument === "options" ? "Option" : "Stock"}
            </span>
            <b>{trade.symbol}</b>
            <StrategyLink
              strategyId={strategy._id}
              name={strategy.name}
              instrument={instrument}
            />
            <span className="pending-actions-hint">{KINDS[kind].hint}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PendingActionsBar;
